import React from 'react'

import Provider from './Provider'
import { IInitiativeManagementViewProps } from './Interfaces'
import { PointrThemedFormItem, Loading, InitiativeSubmissionFormItem } from 'components'
import InitiativeSubmissionContainer from 'components/initiative-submission-container/View'
import useInitiativeManagementStyles from './Styles'
import { FormControlLabel, Button, Checkbox } from '@material-ui/core'
import { ConfirmAction } from 'smart-components/confirm-action'
import IInitiativeSubmissionQuestions, { QuestionType } from 'types/IInitiativeSubmissionQuestions'
import _ from 'lodash'

const InitiativeManagementPage: React.FC<IInitiativeManagementViewProps> = ({
  setGraduateGpaValue,
  setQuestionAnswer,
  setQuestionChoice,
  setUndergraduateGpaValue,
  submitChanges,
  setDoesNotApplyForGraduateGpa,
  setDoesNotApplyForUndergraduateGpa,
  withdrawHandler,
  state,
  withdrawConfirmationMessage,
  withdrawButtonText
}) => {
  const classes = useInitiativeManagementStyles({})

  const onFormItemChange = (item: IInitiativeSubmissionQuestions) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    selected?: boolean
  ) => {
    if (item.questionType === QuestionType.CHECKBOX) {
      if (selected) {
        item.choices.push(+event.target.value)
      } else {
        item.choices = item.choices.filter(choice => choice !== +event.target.value)
      }
      setQuestionChoice(item.id, _.uniq(item.choices))
    } else if (item.questionType === QuestionType.MULTIPLE_CHOICE) {
      const choices = selected ? [+event.target.value] : []
      setQuestionChoice(item.id, choices)
    } else {
      setQuestionAnswer(item.id, event.target.value)
    }
  }

  if (state.isLoadingInitiative || !state.initiativeData) {
    return (
      <div className={classes.loading}>
        <Loading />
      </div>
    )
  }

  return (
    <React.Fragment>
      <InitiativeSubmissionContainer
        title={state.initiativeData?.title}
        submissionPageCopyText={state.initiativeData?.submissionPageCopyText}
        onWithdrawButtonClick={withdrawHandler}
        withdrawButtonText={withdrawButtonText}
        withdrawConfirmationMessage={withdrawConfirmationMessage}
        hasWithdrawButton
        questions={
          <React.Fragment>
            {(state.initiativeData?.questions || []).map((item, index) => (
              <InitiativeSubmissionFormItem
                key={index}
                classes={{ label: classes.questionLabel }}
                item={item}
                value={state.answers.questions[item.id].answer}
                onChange={onFormItemChange(item)}
                selected={state.answers.questions[item.id].choices}
              />
            ))}
          </React.Fragment>
        }
        hasGpaQuestions={state.initiativeData?.hasGpaQuestion}
        gpaQuestions={
          <React.Fragment>
            <div className={classes.gpaQuestionsContainer}>
              <div className={classes.gpaGradeContainer}>
                <div className={classes.gpaValueInputContainer}>
                  <PointrThemedFormItem
                    label="Graduate GPA"
                    value={state.answers?.gpaValues?.graduateGpa?.toString()}
                    placeholder="out of 4.0"
                    disabled={state.answers.gpaValues.doesNotApplyForGraduateGpa}
                    type="number"
                    onChange={event => setGraduateGpaValue(event.target.value)}
                  />
                </div>
                <div className={classes.gpaValueCheckboxContainer}>
                  <FormControlLabel
                    className={classes.checkboxButtonLabelContainer}
                    classes={{ label: classes.checkboxButtonLabel }}
                    control={
                      <Checkbox
                        onChange={event => setDoesNotApplyForGraduateGpa(event.target.checked)}
                        checked={state.answers.gpaValues.doesNotApplyForGraduateGpa}
                        classes={{
                          root: classes.checkboxButtonChecked
                        }}
                      />
                    }
                    label="My school doesn't use this grading system"
                  />
                </div>
              </div>
              <div className={classes.gpaGradeContainer}>
                <div className={classes.gpaValueInputContainer}>
                  <PointrThemedFormItem
                    label="Undergraduate GPA"
                    value={state.answers?.gpaValues?.undergraduateGpa?.toString()}
                    placeholder="out of 4.0"
                    disabled={state.answers.gpaValues.doesNotApplyForUndergraduateGpa}
                    type="number"
                    onChange={event => setUndergraduateGpaValue(event.target.value)}
                  />
                </div>
                <div className={classes.gpaValueCheckboxContainer}>
                  <FormControlLabel
                    className={classes.checkboxButtonLabelContainer}
                    classes={{ label: classes.checkboxButtonLabel }}
                    control={
                      <Checkbox
                        onChange={event => setDoesNotApplyForUndergraduateGpa(event.target.checked)}
                        checked={state.answers?.gpaValues?.doesNotApplyForUndergraduateGpa}
                        classes={{
                          root: classes.checkboxButtonChecked
                        }}
                      />
                    }
                    label="My school doesn't use this grading system"
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        }
        bottomBar={
          <div className={classes.bottomBarRoot}>
            <div className={classes.bottomBarContent}>
              <p className={classes.bottomBarTextContainer}>
                You can save your submission after finishing your changes.
              </p>
              <ConfirmAction
                title="Confirm Changes"
                content="Are you ready to update your submission"
                onConfirm={submitChanges}
              >
                {(isOpen: boolean, toggleDialog: () => void) => (
                  <Button
                    variant="contained"
                    onClick={toggleDialog}
                    disabled={
                      state.isLoadingInitiative || state.isSubmittingInitiative || state.isWithdrawing || isOpen
                    }
                  >
                    Save
                  </Button>
                )}
              </ConfirmAction>
            </div>
          </div>
        }
      />
    </React.Fragment>
  )
}

export default ({ enterpriseId, code }: { enterpriseId: string; code: string }) => (
  <Provider enterpriseId={enterpriseId} submissionLinkCode={code}>
    {props => <InitiativeManagementPage {...props} />}
  </Provider>
)
